import i18next from 'i18next'
import React from 'react'
import {
  AuthButtonPrimary,
  AuthButtonSecondary,
  FormControls,
  FormNotification,
  formSkeletonConfig,
  FormSkeletonLayout
} from '../../../utils/forms'
import { GraduationYearLayout } from '../../../forms/graduationYear/graduationYearLayout'
import { getClassName } from '../../../utils/utils'
import { RootLayout } from '../rootLayout'
import { authOptions } from '../rootWidget'
import { routeUpdaters } from '../routes/routesState'
import { FormInput, SchoolsData } from './studentSubscriptionState'

type StudentSubscriptionLayoutProps = {
  isLoggedIn: boolean
  formInput: Partial<FormInput>
  onChange: { [field in keyof FormInput]: (newValue: string | boolean) => void }
  showFormInputValidation: { [field in keyof FormInput]: boolean }
  onBlur: { [field in keyof FormInput]: () => void }
  isValid: { [field in keyof FormInput]: boolean }
  onSubmit: () => void
} & (
    | {
      step: 'loginWall'
    }
    | {
      step: 'enterSchool'
      schools: SchoolsData | 'loading' | 'error'
      submitStatus: 'not submitted' | 'submitting' | 'error'
    }
    | {
      step: 'checkCode'
      endDate?: string
      submitStatus:
      | 'invalid code'
      | 'too many attempts'
      | 'not submitted'
      | 'submitting'
      | 'account already exists'
      | 'error'
      | 'success and paused subscription'
      | 'success'
    }
  )

export const StudentSubscriptionLayout = (
  props: StudentSubscriptionLayoutProps
) => (
  <FormSkeletonLayout
    type="student-subscription"
    options={authOptions}
    kind="text-image"
    image={formSkeletonConfig.imageDark}
    isClrInverted
    onSubmit={(e) => {
      e.preventDefault()
      props.onSubmit()
    }}
  >
    {props.step === "loginWall" && !props.isLoggedIn ? (
      <>
        <header
          className={getClassName(authOptions, 'headerClassName', 'mb-4')}
        >
          <h1 className={getClassName(authOptions, 'titleClassName', 'mb-4')}>
            {i18next.t('studentSubscription:step0Title')}
          </h1>
          <p className={getClassName(authOptions, 'introClassName', 'lead')}>
            {i18next.t('studentSubscription:step0Intro')}
          </p>
        </header>

        <div className={getClassName(authOptions, 'formActionsClassName', '')}>
          <div className={getClassName(authOptions, 'clickableWrapperClassName', '')}>
            <AuthButtonPrimary
              kind="link"
              label={i18next.t('auth:loginHeader')}
              disabled={false}
              to={routeUpdaters(i18next.language).login.url + `?originPath=${window.location.pathname}`}
            />
          </div>

          <div className={getClassName(authOptions, 'clickableWrapperClassName', '')}>
            <AuthButtonSecondary
              kind="link"
              label={i18next.t('studentSubscription:step0Submit')}
              disabled={false}
              to={routeUpdaters(i18next.language).registerStudent.url + `?originPath=${window.location.pathname}`}
            />
          </div>
        </div>
      </>
    ) : props.step === 'enterSchool' ? (
      // ----- Step 1
      <>
        <header
          className={getClassName(authOptions, 'headerClassName', 'mb-4')}
        >
          <h1 className={getClassName(authOptions, 'titleClassName', 'mb-4')}>
            {i18next.t('studentSubscription:step1Title')}
          </h1>
          <p className={getClassName(authOptions, 'introClassName', 'lead')}>
            {i18next.t('studentSubscription:step1Intro')}
          </p>
          <p
            className={getClassName(authOptions, 'introClassName', 'lead')}
            dangerouslySetInnerHTML={{
              __html: i18next.t('studentSubscription:step1IntroExtra'),
            }}
          />
        </header>

        {(props.submitStatus === 'error' || props.schools === 'error') && (
          <FormNotification
            options={authOptions}
            message={i18next.t('auth:defaultErrorNotification')}
          />
        )}

        {/* First name */}
        <FormControls.FormItemInGroup
          controlId="firstName"
          label={i18next.t('auth:firstName')}
          type="text"
          required={true}
          readOnly={props.submitStatus === 'submitting'}
          value={props.formInput.name}
          onChange={props.onChange.name}
          onBlur={props.onBlur.name}
          validation={
            props.showFormInputValidation.name && !props.isValid.name
              ? {
                kind: 'failed',
                message: i18next.t('auth:signupNameError'),
              }
              : undefined
          }
        />

        {/* Last name */}
        <FormControls.FormItemInGroup
          controlId="surname"
          label={i18next.t('auth:surname')}
          type="text"
          required={true}
          readOnly={props.submitStatus === 'submitting'}
          value={props.formInput.surname}
          onChange={props.onChange.surname}
          onBlur={props.onBlur.surname}
          validation={
            props.showFormInputValidation.surname && !props.isValid.surname
              ? {
                kind: 'failed',
                message: i18next.t('auth:signupSurnameError'),
              }
              : undefined
          }
        />

        {/* School */}
        {props.schools === 'loading' ? (
          <RootLayout.Loader showText hasWrapper />
        ) : (
          <FormControls.FormSelectInGroup
            controlId="studentSchool"
            label={i18next.t('studentSubscription:school')}
            required={true}
            readOnly={props.submitStatus === 'submitting'}
            value={props.formInput.schoolId?.toString() || ""}
            onChange={props.onChange.schoolId}
            onBlur={props.onBlur.schoolId}
            validation={
              props.showFormInputValidation.schoolId && !props.isValid.schoolId
                ? {
                  kind: 'failed',
                  message: i18next.t('studentSubscription:schoolError'),
                }
                : undefined
            }
          >
            {[
              <option value="" disabled={true} key="placeholder">
                {i18next.t('auth:selectPlaceholder', {
                  entity: i18next.t('studentSubscription:school').toLowerCase(),
                })}
              </option>,
              ...(props.schools === 'error'
                ? []
                : props.schools.sort((a, b) => a.Title.localeCompare(b.Title)).map((school) => (
                  <option value={school.Id} key={school.Id}>
                    {school.Title}
                  </option>
                ))),
            ]}
          </FormControls.FormSelectInGroup>
        )}

        {/* Graduation year */}
        <GraduationYearLayout
          readOnly={props.submitStatus === 'submitting'}
          value={props.formInput.graduationYear}
          onChange={props.onChange.graduationYear}
          onBlur={props.onBlur.graduationYear}
          isValid={
            !props.showFormInputValidation.graduationYear ||
            props.isValid.graduationYear
          }
        />

        <div className={getClassName(authOptions, 'formActionsClassName', '')}>
          <div className={getClassName(authOptions, 'clickableWrapperClassName', '')}>
            <AuthButtonPrimary
              kind="button"
              type="submit"
              label={
                props.submitStatus === 'submitting' ? (
                  <FormControls.SubmittingSpinner />
                ) : (
                  i18next.t('studentSubscription:step1Submit')
                )
              }
              disabled={
                !props.isValid.email ||
                !props.isValid.graduationYear ||
                !props.isValid.schoolId ||
                props.submitStatus === 'submitting'
              }
              onClick={() => { }}
            />
          </div>
        </div>
      </>
    ) : props.step === "checkCode" ? (
      // ----- Step 2
      <>
        {(props.submitStatus === 'success' ||
          props.submitStatus === 'success and paused subscription') &&
          props.endDate != undefined ? (
          <>
            <header
              className={getClassName(authOptions, 'headerClassName', 'mb-4')}
            >
              <h1 className={getClassName(authOptions, 'titleClassName', 'mb-4')}>
                {i18next.t(
                  `studentSubscription:step2${props.isLoggedIn ? 'Existing' : 'New'
                  }Title`
                )}
              </h1>
            </header>

            <FormNotification
              status="success"
              options={authOptions}
              message={i18next.t(
                `studentSubscription:submitSuccess${props.submitStatus === 'success and paused subscription'
                  ? 'WithPausedSubscription'
                  : ''
                }Notification`,
                {
                  terminationDate: props.endDate,
                }
              )}
            />

            <div
              className={getClassName(authOptions, 'formActionsClassName', '')}
            >
              <div
                className={getClassName(
                  authOptions,
                  'clickableWrapperClassName',
                  ''
                )}
              >
                <AuthButtonPrimary
                  kind="link"
                  to="/"
                  label={i18next.t('auth:signupSuccessButtonToHome')}
                  disabled={false}
                />
              </div>
            </div>
          </>
        ) : (
          <>
            <header
              className={getClassName(authOptions, 'headerClassName', 'mb-4')}
            >
              <h1 className={getClassName(authOptions, 'titleClassName', 'mb-4')}>
                {i18next.t('studentSubscription:step2ExistingTitle')}
              </h1>
              <p
                className={getClassName(authOptions, 'introClassName', 'lead')}
                dangerouslySetInnerHTML={{ __html: i18next.t('studentSubscription:step2ExistingIntro') }}
              />
            </header>

            {props.submitStatus === 'error' && (
              <FormNotification
                options={authOptions}
                message={i18next.t('auth:defaultErrorNotification')}
              />
            )}

            {props.submitStatus === 'too many attempts' && (
              <FormNotification
                options={authOptions}
                message={i18next.t('studentSubscription:tooManyAttemptsError')}
              />
            )}

            {props.submitStatus === 'invalid code' && (
              <FormNotification
                options={authOptions}
                message={i18next.t('studentSubscription:invalidCodeError')}
              />
            )}

            {/* Code */}
            <FormControls.FormItemInGroup
              controlId="studentCode"
              label={i18next.t('studentSubscription:code')}
              type="text"
              required={true}
              readOnly={props.submitStatus === 'submitting'}
              value={props.formInput.code}
              onChange={props.onChange.code}
              onBlur={props.onBlur.code}
              validation={
                props.showFormInputValidation.code && !props.isValid.code
                  ? {
                    kind: 'failed',
                    message: i18next.t('studentSubscription:codeError'),
                  }
                  : undefined
              }
            />

            <div
              className={getClassName(authOptions, 'formActionsClassName', '')}
            >
              <div
                className={getClassName(
                  authOptions,
                  'clickableWrapperClassName',
                  ''
                )}
              >
                <AuthButtonPrimary
                  kind="button"
                  type="submit"
                  label={
                    props.submitStatus === 'submitting' ? (
                      <FormControls.SubmittingSpinner />
                    ) : (
                      i18next.t(
                        `studentSubscription:step2${props.isLoggedIn ? 'Existing' : 'New'
                        }Submit`
                      )
                    )
                  }
                  disabled={
                    !props.isValid.name ||
                    !props.isValid.surname ||
                    !props.isValid.code ||
                    props.submitStatus === 'submitting'
                  }
                  onClick={() => { }}
                />
              </div>
            </div>
          </>
        )}
      </>
    ) : <></>}

    <footer className={getClassName(authOptions, 'footerClassName', 'mt-4')}>
      <p className={getClassName(authOptions, 'footerItemClassName', 'mb-1')}>
        {i18next.t('auth:assistanceNeeded')}
      </p>
    </footer>
  </FormSkeletonLayout>
)
